import {
  useGetAdminStatsQuery,
  useGetAdminUsersQuery,
  useSearchAdminUsersQuery,
  useElevateUserToAdminMutation,
  useDemoteUserFromAdminMutation,
} from "../store/api/mlbApi";

export const useAdminStats = () => {
  const { data, error, isLoading, refetch } = useGetAdminStatsQuery();

  return {
    stats: data?.stats || null,
    error: error?.data?.error || error?.message,
    isLoading,
    refetch,
    isAdminError: error?.data?.code === "ADMIN_REQUIRED",
  };
};

export const useAdminUsers = (paginationParams = {}) => {
  const { data, error, isLoading, refetch } =
    useGetAdminUsersQuery(paginationParams);

  return {
    users: data?.users || [],
    totalResults: data?.totalResults || 0,
    currentPage: data?.currentPage || 1,
    pageSize: data?.pageSize || 25,
    totalPages: data?.totalPages || 0,
    error: error?.data?.error || error?.message,
    isLoading,
    refetch,
    isAdminError: error?.data?.code === "ADMIN_REQUIRED",
  };
};

export const useSearchUsers = (searchQuery) => {
  const { data, error, isLoading, refetch } = useSearchAdminUsersQuery(
    searchQuery,
    {
      skip: !searchQuery || searchQuery.trim().length === 0,
    }
  );

  return {
    users: data?.users || [],
    error: error?.data?.error || error?.message,
    isLoading,
    refetch,
  };
};

const roleError = (err, fallback) =>
  err?.data?.error || err?.message || fallback;

export const useChangeAdminRole = () => {
  const [elevateUser, elevateState] = useElevateUserToAdminMutation();
  const [demoteUser, demoteState] = useDemoteUserFromAdminMutation();

  const changeAdminRole = async (userId, makeAdmin) => {
    try {
      const mutate = makeAdmin ? elevateUser : demoteUser;
      const result = await mutate(userId).unwrap();
      return { success: true, data: result };
    } catch (err) {
      return {
        success: false,
        error: roleError(
          err,
          makeAdmin ? "Failed to grant admin access" : "Failed to remove admin access"
        ),
        code: err?.data?.code,
      };
    }
  };

  return {
    changeAdminRole,
    isLoading: elevateState.isLoading || demoteState.isLoading,
    error:
      roleError(elevateState.error, null) || roleError(demoteState.error, null),
  };
};

export const useElevateUser = () => {
  const { changeAdminRole, isLoading, error } = useChangeAdminRole();
  return {
    elevateUserToAdmin: (userId) => changeAdminRole(userId, true),
    isLoading,
    error,
  };
};
